/**
 * Pure H OS · Sprint 3 · Campaign Calendar (list + timeline).
 */
import { useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, SectionHeader } from "../../components/lib";
import { TabBar } from "../../components/lib.overlay";
import { CAMPAIGNS, CampaignStatus } from "../../mocks/marketingData";

const STATUS_LABEL: Record<CampaignStatus, string> = {
  "planned":   "Planned",
  "in-flight": "In flight",
  "live":      "Live",
  "wrapping":  "Wrapping",
  "done":      "Done",
};

const STATUS_TINT: Record<CampaignStatus, { bg: string; fg: string }> = {
  "planned":   { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)"      },
  "in-flight": { bg: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)"  },
  "live":      { bg: "var(--ph-success-tint)", fg: "var(--ph-success)"        },
  "wrapping":  { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)"        },
  "done":      { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-soft)"       },
};

// Timeline computes month grid from a reference date
const REF_START = new Date("2026-05-01");
const REF_END   = new Date("2026-09-01");

function toPct(d: Date): number {
  const t = d.getTime();
  return ((t - REF_START.getTime()) / (REF_END.getTime() - REF_START.getTime())) * 100;
}

const MONTHS = ["May", "Jun", "Jul", "Aug"];

export default function Calendar() {
  const [tab, setTab] = useState<"timeline" | "list">("timeline");
  const [filter, setFilter] = useState<CampaignStatus | "all">("all");

  const rows = useMemo(() => filter === "all" ? CAMPAIGNS : CAMPAIGNS.filter((c) => c.status === filter), [filter]);
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: CAMPAIGNS.length };
    CAMPAIGNS.forEach((x) => { c[x.status] = (c[x.status] || 0) + 1; });
    return c;
  }, []);

  return (
    <div className="space-y-6" data-testid="mkt-calendar">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <TabBar<"timeline" | "list">
          testId="mkt-cal-view"
          active={tab}
          onChange={setTab}
          tabs={[{ key: "timeline", label: "Timeline" }, { key: "list", label: "List" }]}
        />
        <TabBar<CampaignStatus | "all">
          testId="mkt-cal-filter"
          active={filter}
          onChange={setFilter}
          tabs={[
            { key: "all",       label: "All",         count: counts.all       },
            { key: "in-flight", label: "In flight",   count: counts["in-flight"] },
            { key: "live",      label: "Live",        count: counts.live      },
            { key: "planned",   label: "Planned",     count: counts.planned   },
            { key: "wrapping",  label: "Wrapping",    count: counts.wrapping  },
            { key: "done",      label: "Done",        count: counts.done      },
          ]}
        />
      </div>

      {tab === "timeline" ? (
        <Card padded={false} testId="mkt-cal-timeline-card">
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
            <SectionHeader
              eyebrow="Quarter view"
              title="May → August 2026"
              action={
                <div className="flex items-center gap-1">
                  <button className="p-1 rounded" style={{ color: "var(--ph-ink-muted)" }}><ChevronLeft size={14} /></button>
                  <button className="p-1 rounded" style={{ color: "var(--ph-ink-muted)" }}><ChevronRight size={14} /></button>
                  <CalendarDays size={14} style={{ color: "var(--ph-ink-muted)" }} />
                </div>
              }
            />
          </div>

          {/* Month header */}
          <div className="px-5 pt-3 pb-2 grid" style={{ gridTemplateColumns: `220px 1fr` }}>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Campaign</div>
            <div className="relative">
              <div className="grid grid-cols-4">
                {MONTHS.map((m) => (
                  <div key={m} className="text-[10px] uppercase tracking-[0.14em] px-2" style={{ color: "var(--ph-ink-muted)" }}>{m}</div>
                ))}
              </div>
            </div>
          </div>

          <div className="p-5 pt-2 space-y-3">
            {rows.map((c) => {
              const start = new Date(c.startDate);
              const end   = new Date(c.endDate);
              const left  = Math.max(0, toPct(start));
              const right = Math.min(100, toPct(end));
              const width = Math.max(4, right - left);
              const tint = STATUS_TINT[c.status];
              return (
                <div key={c.id} data-testid={`mkt-cal-row-${c.id}`} className="grid items-center" style={{ gridTemplateColumns: `220px 1fr` }}>
                  <div className="min-w-0 pr-3">
                    <div className="text-sm font-semibold truncate" style={{ color: "var(--ph-ink-strong)" }}>{c.name}</div>
                    <div className="text-[11px] mt-0.5 truncate" style={{ color: "var(--ph-ink-muted)" }}>{c.sku} · {c.channels[0]}</div>
                  </div>
                  <div className="relative h-8 rounded" style={{ background: "var(--ph-surface)", border: "1px solid var(--ph-border-subtle)" }}>
                    {/* month gridlines */}
                    {[25, 50, 75].map((p) => (
                      <div key={p} className="absolute top-0 bottom-0" style={{ left: `${p}%`, width: 1, background: "var(--ph-border-subtle)" }} />
                    ))}
                    <div
                      className="absolute top-1 bottom-1 flex items-center px-2"
                      style={{
                        left: `${left}%`,
                        width: `${width}%`,
                        background: tint.bg,
                        border: `1px solid ${c.color}`,
                        borderLeft: `3px solid ${c.color}`,
                        borderRadius: "var(--ph-radius-sm)",
                        overflow: "hidden",
                      }}
                    >
                      <span className="text-[10px] font-semibold truncate" style={{ color: tint.fg }}>
                        {STATUS_LABEL[c.status]} · ROAS {c.roas.toFixed(1)}×
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      ) : (
        <Card padded={false} testId="mkt-cal-list-card">
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
            <SectionHeader eyebrow="List" title={`${rows.length} campaign${rows.length === 1 ? "" : "s"}`} />
          </div>
          <ul className="divide-y" style={{ borderColor: "var(--ph-border-subtle)" }}>
            {rows.map((c) => {
              const tint = STATUS_TINT[c.status];
              return (
                <li key={c.id} data-testid={`mkt-cal-list-${c.id}`} className="p-5 flex items-start gap-4">
                  <div style={{ width: 4, height: 40, borderRadius: 2, background: c.color, flexShrink: 0 }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <div className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{c.name}</div>
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase" style={{ background: tint.bg, color: tint.fg, borderRadius: "var(--ph-radius-sm)", letterSpacing: "0.14em" }}>
                        {STATUS_LABEL[c.status]}
                      </span>
                    </div>
                    <div className="text-[11px] mt-1" style={{ color: "var(--ph-ink-muted)" }}>
                      {c.sku} · {c.channels.join(" · ")} · owner {c.owner} · {c.startDate} → {c.endDate}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>
                      EGP {(c.budgetEGP / 1000).toFixed(0)}K
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Budget</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
